import {
	AnimatePresence,
	motion,
	type PanInfo,
	useReducedMotion,
} from "motion/react";
import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./BottomSheet.module.css";

interface BottomSheetBaseProps {
	isOpen: boolean;
	onClose: () => void;
	header?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	dragToCloseEnabled?: boolean;
	/** 'auto' (content-driven, capped at 95vh) | 'tall' (90vh, near-full-screen — Phase 36.1 D-01) | number (px) | string (CSS height) */
	panelHeight?: "auto" | "tall" | number | string;
}

// Exactly one of ariaLabelledBy or ariaLabel is required so the dialog is
// always labeled for screen readers.
type BottomSheetLabeling =
	| { ariaLabelledBy: string; ariaLabel?: never }
	| { ariaLabel: string; ariaLabelledBy?: never };

export type BottomSheetProps = BottomSheetBaseProps & BottomSheetLabeling;

export function BottomSheet({
	isOpen,
	onClose,
	header,
	children,
	footer,
	dragToCloseEnabled = true,
	panelHeight = "auto",
	ariaLabelledBy,
	ariaLabel,
}: BottomSheetProps) {
	const prefersReducedMotion = useReducedMotion();
	// ESC + body scroll lock — extracted verbatim from SuggestedQuestionsOverlay
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	const handleDragEnd = (
		_e: MouseEvent | TouchEvent | PointerEvent,
		info: PanInfo,
	) => {
		if (info.offset.y > 100 || info.velocity.y > 500) onClose();
	};

	const sheetStyle = {
		height:
			panelHeight === "auto"
				? "auto"
				: panelHeight === "tall"
					? "90vh"
					: typeof panelHeight === "number"
						? `${panelHeight}px`
						: panelHeight,
		maxHeight: "95vh",
		minHeight: "360px",
	};

	// Portal to <body> so the sheet escapes any ancestor stacking context
	// (PrimeReact / Capacitor wrappers) — otherwise the root-level fixed
	// BottomNav paints over the sheet's backdrop and footer despite the higher
	// z-index, hiding the action buttons (e.g. D2C Ask Eve / Mark completed).
	return createPortal(
		<AnimatePresence>
			{isOpen && (
				<>
					<motion.div
						className={styles.backdrop}
						initial={prefersReducedMotion ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
						transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
						onClick={onClose}
					/>
					<div className={styles.sheetContainer}>
						<motion.div
							className={styles.sheetPanel}
							role="dialog"
							aria-modal="true"
							aria-labelledby={ariaLabelledBy}
							aria-label={ariaLabel}
							style={sheetStyle}
							initial={prefersReducedMotion ? false : { y: "100%" }}
							animate={{ y: 0 }}
							exit={prefersReducedMotion ? { y: 0 } : { y: "100%" }}
							transition={
								prefersReducedMotion
									? { duration: 0 }
									: { type: "spring", damping: 25, stiffness: 300 }
							}
						>
							{dragToCloseEnabled && (
								<motion.div
									className={styles.dragHandleArea}
									drag="y"
									dragConstraints={{ top: 0, bottom: 0 }}
									dragElastic={{ top: 0, bottom: 0.5 }}
									onDragEnd={handleDragEnd}
								>
									<div className={styles.dragHandle} />
								</motion.div>
							)}
							{header && <div className={styles.header}>{header}</div>}
							<div className={styles.body}>{children}</div>
							{footer && <div className={styles.footer}>{footer}</div>}
						</motion.div>
					</div>
				</>
			)}
		</AnimatePresence>,
		document.body,
	);
}
